<template>
  <div class="main-content-container container-fluid px-4">
    <!-- Breadcrumbs -->
    <nav aria-label="breadcrumb" class="mb-3">
      <ol class="breadcrumb">
        <li class="breadcrumb-item"><a href="#" @click.prevent="$router.push({ name: 'widgets' })">Widgets</a></li>
        <li class="breadcrumb-item active">{{ isEditMode ? 'Edit Widget' : 'Create Widget' }}</li>
      </ol>
    </nav>

    <!-- Page Header -->
    <div class="page-header row no-gutters py-4">
      <div class="col-12 col-sm-4 text-center text-sm-left mb-0">
        <h3 class="page-title">{{ isEditMode ? 'Edit Widget' : 'Create Widget' }}</h3>
      </div>
      <div class="col-12 col-sm-8 text-center text-sm-right">
        <d-button theme="secondary" @click="cancel">Cancel</d-button>
        <d-button theme="primary" @click="save" :disabled="saving">
          <i class="material-icons mr-1">save</i> {{ saving ? 'Saving...' : 'Save' }}
        </d-button>
      </div>
    </div>

    <!-- Widget Form -->
    <div class="row">
      <div class="col-lg-8">
        <div class="card card-small mb-4">
          <div class="card-header border-bottom">
            <h6 class="m-0">Widget Configuration</h6>
          </div>
          <div class="card-body">
            <div class="form-group">
              <label for="widget-name">Name <span class="text-danger">*</span></label>
              <input
                id="widget-name"
                v-model="widget.name"
                type="text"
                class="form-control"
                placeholder="e.g., Homepage Recommendations"
                required
              />
            </div>

            <div class="form-group">
              <label for="widget-description">Description</label>
              <textarea
                id="widget-description"
                v-model="widget.description"
                class="form-control"
                rows="3"
                placeholder="Describe this widget..."
              />
            </div>

            <div class="form-group">
              <label for="widget-type">Widget Type <span class="text-danger">*</span></label>
              <select id="widget-type" v-model="widget.type" class="form-control" required>
                <option value="">Select type...</option>
                <option value="recommendations">Recommendations</option>
                <option value="metrics">Metrics</option>
                <option value="chart">Chart</option>
                <option value="table">Table</option>
              </select>
            </div>

            <div class="form-group" v-if="widget.type === 'recommendations'">
              <label for="widget-scenario">Scenario Name</label>
              <input
                id="widget-scenario"
                v-model="widget.scenario_name"
                type="text"
                class="form-control"
                placeholder="e.g., homepage-featured"
              />
            </div>

            <!-- Widget Config -->
            <div class="form-group">
              <label>Widget Configuration</label>
              <div class="card">
                <div class="card-body">
                  <div class="form-group">
                    <label>Title</label>
                    <input v-model="widgetConfig.title" type="text" class="form-control" />
                  </div>
                  <div class="form-group" v-if="widget.type === 'recommendations'">
                    <label>User ID (for preview)</label>
                    <input v-model="widgetConfig.user_id" type="text" class="form-control" placeholder="user-123" />
                  </div>
                  <div class="form-group" v-if="widget.type === 'recommendations'">
                    <label>Item ID (for item-to-item recommendations)</label>
                    <input v-model="widgetConfig.item_id" type="text" class="form-control" placeholder="item-456" />
                  </div>
                  <div class="form-group" v-if="widget.type === 'recommendations'">
                    <label>Count</label>
                    <input v-model.number="widgetConfig.count" type="number" class="form-control" min="1" max="50" />
                  </div>
                  <div class="form-group" v-if="widget.type === 'recommendations'">
                    <label>Layout</label>
                    <select v-model="widgetConfig.layout" class="form-control">
                      <option value="grid">Grid</option>
                      <option value="list">List</option>
                      <option value="carousel">Carousel</option>
                    </select>
                  </div>
                  <div class="form-group">
                    <div class="form-check">
                      <input v-model="widgetConfig.show_title" type="checkbox" class="form-check-input" id="show-title" />
                      <label class="form-check-label" for="show-title">Show Title</label>
                    </div>
                  </div>
                  <div class="form-group">
                    <div class="form-check">
                      <input v-model="widgetConfig.show_description" type="checkbox" class="form-check-input" id="show-description" />
                      <label class="form-check-label" for="show-description">Show Description</label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="form-group">
              <div class="form-check">
                <input v-model="widget.enabled" type="checkbox" class="form-check-input" id="widget-enabled" />
                <label class="form-check-label" for="widget-enabled">Enabled</label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Preview -->
      <div class="col-lg-4" v-if="widget.type">
        <div class="card card-small mb-4">
          <div class="card-header border-bottom">
            <h6 class="m-0">Preview</h6>
          </div>
          <div class="card-body">
            <div class="text-muted text-center py-4">
              <i class="material-icons" style="font-size: 48px;">widgets</i>
              <p class="mt-2">Preview will appear here</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import widgetsAPI from '@/api/widgets';
import scenariosAPI from '@/api/scenarios';

export default {
  name: 'WidgetEditor',
  data() {
    return {
      widget: {
        name: '',
        description: '',
        type: '',
        scenario_name: '',
        enabled: true,
      },
      widgetConfig: {
        title: '',
        user_id: '',
        item_id: '',
        count: 10,
        layout: 'grid',
        show_title: true,
        show_description: false,
      },
      scenarios: [],
      saving: false,
    };
  },
  computed: {
    isEditMode() {
      return !!this.$route.params.id;
    },
  },
  created() {
    this.loadScenarios();
    if (this.isEditMode) {
      this.loadWidget();
    }
  },
  methods: {
    loadScenarios() {
      scenariosAPI.getScenarios('', 1000)
        .then((response) => {
          this.scenarios = response.data.scenarios || [];
        })
        .catch((error) => {
          console.error('Failed to load scenarios:', error);
        });
    },
    loadWidget() {
      const id = parseInt(this.$route.params.id);
      widgetsAPI.getWidget(id)
        .then((response) => {
          const data = response.data;
          this.widget = {
            name: data.name || '',
            description: data.description || '',
            type: data.type || '',
            scenario_name: data.scenario_name || '',
            enabled: data.enabled !== undefined ? data.enabled : true,
          };
          if (data.config) {
            this.widgetConfig = {
              title: data.config.title || '',
              user_id: data.config.user_id || '',
              item_id: data.config.item_id || '',
              count: data.config.count || 10,
              layout: data.config.layout || 'grid',
              show_title: data.config.show_title !== undefined ? data.config.show_title : true,
              show_description: data.config.show_description || false,
            };
          }
        })
        .catch((error) => {
          console.error('Failed to load widget:', error);
          alert('Failed to load widget: ' + (error.response && error.response.data && error.response.data.error || error.message));
        });
    },
    save() {
      // Validate
      if (!this.widget.name || !this.widget.type) {
        alert('Please fill in all required fields');
        return;
      }

      this.saving = true;
      const payload = {
        ...this.widget,
        config: this.widgetConfig,
      };

      const promise = this.isEditMode
        ? widgetsAPI.updateWidget(parseInt(this.$route.params.id), payload)
        : widgetsAPI.createWidget(payload);

      promise
        .then(() => {
          this.$router.push({ name: 'widgets' });
        })
        .catch((error) => {
          console.error('Failed to save widget:', error);
          alert('Failed to save widget: ' + (error.response && error.response.data && error.response.data.error || error.message));
        })
        .finally(() => {
          this.saving = false;
        });
    },
    cancel() {
      this.$router.push({ name: 'widgets' });
    },
  },
};
</script>

